import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './style.module.css';
import moment from 'moment-timezone';
import Button from '../Button';
import { useNavigate } from 'react-router-dom';
import { getBotDetails } from '../../services/kollectaApi';
import { assumeConversation } from '../../services/kollectaApi';
import FileMessage from '../FileMessage';
import { getWhatsappHistoryBatch, getWhatsappHistory } from '../../services/kollectaApi';

const WhatsappHistory = ({ bot, cellphone, customerData }) => {
  const [messages, setMessages] = useState([]);
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false);
  const [validatedBot, setValidatedBot] = useState(null);
  const [hasRecentActivity, setHasRecentActivity] = useState(true);
  const chatContainerRef = useRef(null);
  const formRef = useRef(null);

  const formatPhoneNumber = (cellphone) => {
    let cleaned = cellphone.replace(/\D/g, '');

    if (/^(\d{2})9(\d{8})$/.test(cleaned)) {
      cleaned = cleaned.replace(/^(\d{2})9(\d{8})$/, '55$1$2');
    } else if (/^55(\d{2})9(\d{8})$/.test(cleaned)) {
      cleaned = cleaned.replace(/^55(\d{2})9(\d{8})$/, '55$1$2');
    }

    if (cleaned.startsWith('55') && cleaned.length === 13) {
      return cleaned;
    }

    if (cleaned.startsWith('55') && (cleaned.length === 12 || cleaned.length === 14)) {
      return cleaned;
    }

    return cleaned;
  };


  const validateBot = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.companyId) {
      const data = {
        companyId: user.companyId,
        botName: bot,
      };

      try {
        const response = await getBotDetails(data?.companyId);

        if (response.data) {
          console.log('BOT: ', response?.data[0].name);
          setValidatedBot(response?.data[0].name || bot);
        } else {
          setValidatedBot(bot);
        }
      } catch (error) {
        console.error('Erro ao validar bot:', error);
        setValidatedBot(bot);
      }
    } else {
      setValidatedBot(bot);
    }
  }, [bot]);

  const loadMessageHistory = useCallback(async () => {
    if (!validatedBot) return;

    setLoading(true);
    try {
      // Função getAllPhoneNumbers movida para dentro do useCallback
      const getAllPhoneNumbers = () => {
        const phoneNumbers = new Set();

        // Função para adicionar número e sua versão com "9"
        const addPhoneWithNine = (phone) => {
          const formatted = formatPhoneNumber(phone);
          phoneNumbers.add(formatted);
          
          // Se o número tem 12 dígitos (sem o 9), adiciona versão com 9
          if (formatted.length === 12 && formatted.startsWith('55')) {
            const withNine = formatted.substring(0, 4) + '9' + formatted.substring(4);
            phoneNumbers.add(withNine);
          }
          // Se o número tem 13 dígitos (com o 9), adiciona versão sem 9
          else if (formatted.length === 13 && formatted.startsWith('55') && formatted.charAt(4) === '9') {
            const withoutNine = formatted.substring(0, 4) + formatted.substring(5);
            phoneNumbers.add(withoutNine);
          }
        };

        if (cellphone) {
          addPhoneWithNine(cellphone);
        }

        if (customerData?.confirmedNumber) {
          addPhoneWithNine(customerData.confirmedNumber);
        }

        if (customerData?.phones && Array.isArray(customerData.phones)) {
          customerData.phones.forEach(phone => {
            addPhoneWithNine(phone);
          });
        }

        return Array.from(phoneNumbers);
      };

      const phoneNumbers = getAllPhoneNumbers();
      console.log('%c phoneNumbers', 'color: #bada55', phoneNumbers);

      // Salvar os números do batch em storage separado
      localStorage.setItem("batchPhoneNumbers", JSON.stringify(phoneNumbers));

      const response = await getWhatsappHistoryBatch({
        bot: validatedBot,
        cellphones: phoneNumbers,
        limit: 10000
      });

      // Capturar hasRecentActivity da resposta
      if (response.data?.hasRecentActivity !== undefined) {
        setHasRecentActivity(response.data.hasRecentActivity);
      }

      if (response.data?.data && Array.isArray(response.data.data)) {
        const sortedMessages = response.data.data
          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        setMessages(sortedMessages);
        console.log('%c messages', 'color: #bada55', sortedMessages);
        console.log('%c hasRecentActivity', 'color: #bada55', response.data.hasRecentActivity);
      } else {
        console.log('%c no messages', 'color: #bada55', response.data);
        setMessages([]);
      }
    } catch (error) {
      console.error('Erro ao carregar histórico de mensagens:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [validatedBot, cellphone, customerData]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      // Múltiplas tentativas para garantir que o scroll funcione
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
      
      // Segunda tentativa com delay maior para garantir que o DOM foi atualizado
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  };

  const updatePaddingBottom = () => {
    if (chatContainerRef.current && formRef.current) {
      chatContainerRef.current.style.paddingBottom = `${formRef.current.offsetHeight}px`;
    }
  };

  useEffect(() => {
    validateBot();
  }, [validateBot]);

  useEffect(() => {
    if (validatedBot) {
      loadMessageHistory();
    }
  }, [validatedBot, loadMessageHistory]);

  // Scroll para o final quando o loading terminar
  useEffect(() => {
    if (!loading && messages.length > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 500);
    }
  }, [loading, messages.length]);

  useEffect(() => {
    if (messages.length > 0) {
      // Aguarda um pouco mais para garantir que o DOM foi completamente renderizado
      setTimeout(() => {
        scrollToBottom();
        updatePaddingBottom();
      }, 200);
    }
  }, [messages]);

  useEffect(() => {
    const handleResize = () => {
      updatePaddingBottom();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!messages || messages.length === 0) {
    return <div>Sem histórico de conversas para esse número</div>;
  }

  console.log('📌 Estado atual de messages:', messages.length, messages);

  const handleAssumirConversa = async () => {
    console.log('🟢 Botão "Assumir conversa" clicado');
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("Token não encontrado");
        return;
      }

      const { companyId, personalNumber, email, customerName } = customerData || {};

      if (!personalNumber) {
        console.log("Identificador do cliente não encontrado");
        return;
      }

      if (!companyId) {
        console.log("CompanyId não encontrado");
        return;
      }

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (!user?.id) {
        console.log("Usuário não encontrado");
        return;
      }

      const agentId = String(user.id);
      
      // Prioriza confirmedNumber, senão usa cellphone
      const phoneToUse = customerData?.confirmedNumber || cellphone;
      const phone = formatPhoneNumber(phoneToUse);

      console.log('Números disponíveis para assumeConversation:');
      console.log('- confirmedNumber:', customerData?.confirmedNumber);
      console.log('- cellphone (prop):', cellphone);
      console.log('- Número escolhido:', phoneToUse);
      console.log('- Número formatado:', phone);

      const response = await getBotDetails(companyId);
      const botData = response?.data?.[0];

      if (!botData?.botNumber) {
        console.log("BotNumber não encontrado para essa empresa");
        return;
      }

      const { name } = botData;

      const routing = {
        phone,
        companyId,
        agentId,
        personalNumber,
        customerName,
        email,
        active: true,
      };

      await assumeConversation({ phone, companyId, personalNumber, agentId });

      localStorage.setItem("routing", JSON.stringify(routing));

      const historyResponse = await getWhatsappHistory(name, phone, companyId);
      const messages = historyResponse.data
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) || [];

      navigate(`/dashboard/chat-human/${personalNumber}`, {
        state: {
          messages,
          routing,
        },
      });
    } catch (error) {
      console.error("Erro ao assumir conversa:", error);
      const status = error?.status ?? error?.response?.status;
      const body = error?.data ?? error?.response?.data;
      const apiMsg =
        (typeof body === "object" && body !== null
          ? body.message || body.error
          : null) ||
        (typeof body === "string" ? body : null);
      const msg =
        apiMsg ||
        (status === 409
          ? "Esta conversa já está sendo atendida por outro operador."
          : "Não foi possível assumir a conversa.");
      alert(msg);
    }
  };


  return (
    <div className={styles.container}>
      <div ref={chatContainerRef} id="chatContainer" className={styles.chatContainer}>
        {messages.map((message, index) => (
          message.text && message.text.trim() !== '' && (
            <div
              key={index}
              className={`${styles.response} ${message.type === 'answer' ? styles.userMessage : styles.assistantMessage}`}
              style={{ alignSelf: message.type === 'answer' ? 'flex-end' : 'flex-start' }}
            >
              <div className={styles.messageText}>
                {message.isFile ? (
                  <FileMessage 
                    base64Content={message.text}
                    fileName={message.fileName || 'arquivo'}
                    fileType={message.fileType}
                  />
                ) : message.type === 'answer' ? (
                  message.text
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: message.text }} />
                )}
              </div>
              <div className={styles.messageDate}>
                {message.createdAt && moment(message.createdAt).format('HH:mm DD/MM')}
              </div>
            </div>
          )
        ))}
      </div>
      {messages.length > 0
        ? console.log('🟢 Renderizando botão, messages.length:', messages.length)
        : console.log('⚪ messages.length = 0, botão não será renderizado')}
      {messages.length > 0 && (
        <div>
          {!hasRecentActivity && (
            <div style={{ 
              padding: '10px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7', 
              borderRadius: '4px', 
              marginBottom: '10px',
              color: '#856404',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              Número não teve atividade recente nas últimas 24 horas
            </div>
          )}
          <Button
            color={'var(--kollecta-purple)'}
            onClick={handleAssumirConversa}
            disabled={!hasRecentActivity}
            style={{ 
              width: "100%",
              marginTop: 20,
              opacity: !hasRecentActivity ? 0.6 : 1,
              cursor: !hasRecentActivity ? 'not-allowed' : 'pointer'
            }}
          >
            Assumir conversa
          </Button>
        </div>
      )}
    </div>
  );
};

export default WhatsappHistory;
